import { Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import { FormEvent, useState } from "react";
import { ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link, Navigate } from "../lib/router";

export function LoginPage() {
  const { admin, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsMfa, setNeedsMfa] = useState(false);

  if (admin) return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password, needsMfa ? mfaCode : undefined);
    } catch (cause) {
      const message =
        cause instanceof ApiError ? cause.message : "Não foi possível entrar.";
      if (message === "invalid_mfa_code") {
        setNeedsMfa(true);
        setError("Introduza o código de autenticação para continuar.");
      } else if (message === "login_temporarily_locked") {
        setError(
          "Demasiadas tentativas. Tente novamente dentro de 15 minutos.",
        );
      } else {
        setError("Email ou palavra-passe incorretos.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-visual">
        <div className="login-visual__grid" />
        <div className="login-brand">
          <img src="/brand/pagaqui.svg" alt="PAGAQUI" />
          <span>Pagaqui Wallet - Backoffice</span>
        </div>
        <div className="login-visual__copy">
          <span className="eyebrow eyebrow--light">
            Pagaqui Wallet - Backoffice
          </span>
          <h1>Uma visão clara de cada cliente.</h1>
          <p>
            Reveja identidades, acompanhe wallets e tome decisões KYC num único
            espaço protegido.
          </p>
        </div>
        <div className="security-note">
          <ShieldCheck size={20} />
          <span>Todas as consultas e decisões são auditadas.</span>
        </div>
      </section>

      <section className="login-panel">
        <form className="login-form" onSubmit={submit}>
          <div className="login-form__icon">
            <LockKeyhole size={24} />
          </div>
          <span className="eyebrow">Acesso reservado</span>
          <h2>Bem-vindo</h2>
          <p>Entre com a sua conta administrativa QUI.</p>

          <label>
            Email profissional
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="nome@pagaqui.pt"
              required
              autoFocus
            />
          </label>

          <label>
            Palavra-passe
            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={
                  showPassword
                    ? "Ocultar palavra-passe"
                    : "Mostrar palavra-passe"
                }
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>
          <Link to="/forgot-password" className="forgot-password-link">
            Esqueceste-te da password?
          </Link>

          {needsMfa && (
            <label>
              Código de autenticação
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(event) =>
                  setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 8))
                }
                placeholder="000 000"
                required
              />
            </label>
          )}

          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}

          <button
            className="button button--primary button--full"
            disabled={busy}
          >
            {busy ? (
              <span className="spinner spinner--button" />
            ) : (
              "Entrar no backoffice"
            )}
          </button>
          <small>
            Acesso exclusivo a colaboradores autorizados. A utilização indevida
            é registada.
          </small>
        </form>
      </section>
    </main>
  );
}
